import React, { useState, useEffect, useRef } from 'react';

const SEVERITY_COLOR = {
  danger: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
};

// Persisted (DB) payload: {id, user_id, message, is_read, created_at}
// SSE push payload: {tip, severity, metin, veri, tarih}
// normalizeNotification: ikisini ortak bir UI şekline indirger.
function normalizeNotification(raw) {
  const isPersisted = raw && raw.id !== undefined && raw.message !== undefined;
  return {
    id: raw && raw.id !== undefined ? raw.id : null, // null for SSE pushes
    text: (raw && (raw.metin ?? raw.message)) || '',
    severity: (raw && raw.severity) || 'info', // persisted items default info
    time: (raw && (raw.tarih ?? raw.created_at)) || null,
    isRead: isPersisted ? Boolean(raw.is_read) : false, // SSE pushes start unread
    isPersisted,
    raw,
  };
}

// Bildirim listesi ve canlı SSE akışı Bearer token ile korunur.
// Native EventSource Authorization header desteklemediği için SSE, fetch stream ile okunur.
const NotificationBell = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const esRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8001';

  // 1) Initial load: persistent notifications (DB)
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return undefined;
    // I-3: clear stale state on user change before fetch (multi-tenant cleanliness)
    setNotifications([]);
    // I-2: read token inside effect (not at render scope)
    const token =
      typeof window !== 'undefined' ? window.localStorage.getItem('access_token') : null;
    let cancelled = false;
    fetch(`${apiUrl}/api/notifications`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const normalized = list.map(normalizeNotification);
        setNotifications(normalized.slice(0, 20));
      })
      .catch(() => {
        // sessizce yoksay — bildirim listesi boş kalır
      });
    return () => {
      cancelled = true;
    };
    // I-1: depend on user?.id (stable primitive), not whole user object
  }, [user?.id, apiUrl]);

  // 2) Authenticated SSE live push (fetch stream + Bearer token)
  useEffect(() => {
    if (!user?.id) return undefined;
    const token =
      typeof window !== 'undefined' ? window.localStorage.getItem('access_token') : null;
    if (!token) return undefined;

    const controller = new AbortController();
    esRef.current = controller;

    const connect = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/notifications/stream`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!response.ok || !response.body) return;

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (!controller.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          events.forEach((eventBlock) => {
            const dataLine = eventBlock
              .split('\n')
              .find((line) => line.startsWith('data:'));
            if (!dataLine) return;
            try {
              const data = JSON.parse(dataLine.slice(5).trim());
              if (data.tip === 'ping' || data.tip === 'heartbeat') return;
              const n = normalizeNotification(data);
              setNotifications((prev) => [n, ...prev].slice(0, 20));
            } catch (err) {
              // hatalı payload — sessizce yoksay
            }
          });
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          // bağlantı hatası — zil, kalıcı bildirim listesiyle çalışmaya devam eder
        }
      }
    };

    connect();
    return () => controller.abort();
  }, [user?.id, apiUrl]);

  const unread = notifications.filter((n) => !n.isRead).length;

  const handleClickItem = async (n) => {
    if (!n.isPersisted || !n.id || n.isRead) return;
    // Optimistic UI update
    setNotifications((prev) =>
      prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x))
    );
    try {
      // I-2: read token at call site (lazy, no render-time side effect)
      const token =
        typeof window !== 'undefined' ? window.localStorage.getItem('access_token') : null;
      await fetch(`${apiUrl}/api/notifications/${n.id}/read`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (err) {
      // best-effort; revert on failure could be added later
    }
  };

  if (!user) return null;

  return (
    <div style={{ position: 'fixed', top: '15px', right: '20px', zIndex: 9999 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          border: 'none',
          background: 'white',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title="Bildirimler"
        aria-label="Bildirimler"
      >
        <span style={{ fontSize: '20px' }}>🔔</span>
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              background: '#ef4444',
              color: 'white',
              borderRadius: '50%',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              border: '2px solid white',
              padding: '0 4px',
            }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '50px',
            width: '320px',
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            maxHeight: '450px',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              padding: '12px',
              borderBottom: '1px solid #eee',
              fontWeight: 'bold',
              color: '#333',
              fontSize: '14px',
            }}
          >
            Bildirimler
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
              Yeni bildirim yok
            </div>
          ) : (
            notifications.map((n, i) => (
              <div
                key={n.id != null ? `db-${n.id}` : `sse-${i}`}
                onClick={() => handleClickItem(n)}
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid #f3f4f6',
                  borderLeft: `3px solid ${SEVERITY_COLOR[n.severity] || '#6b7280'}`,
                  background: n.isRead ? 'white' : '#fefce8',
                  cursor: n.isPersisted && !n.isRead ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{
                    fontSize: '13px',
                    color: '#111',
                    fontWeight: n.isRead ? 400 : 600,
                  }}
                >
                  {n.text}
                </div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                  {n.time ? new Date(n.time).toLocaleTimeString('tr-TR') : ''}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
